// /campaigns/launch — create and fund a campaign as a step-by-step flow (one
// question per screen, like Instagram's boost flow), ending in a review and
// Stripe Checkout for the clipper pool (the 30% fee comes out of clipper payouts). ?edit=<id> reuses the
// same steps to edit a brief. Every answer is a tap: name and avatar come
// from the connected channel, and the title, pitch and overview are written
// from the picks. The one text box is pasting a VOD link.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { axiosInstance } from "../../config";
import {
  PLATFORM_FEE_RATE, PLATFORM_META, PLATFORM_POST_LABEL, POST_PLATFORMS,
  briefPath, fmtMoney,
} from "./campaignsShared";
import { parseFundPrompt } from "./parseFundPrompt";

const CATEGORIES = ["Gaming", "Entertainment", "Music", "Sports", "Education", "Lifestyle", "Technology", "Other"];
const DRAFT_KEY = "cb_campaign_launch_draft";
const MIN_POOL = 1000; // mirrors routes/campaigns.js
// Asked first, like an ad objective. Leads and sales campaigns also ask where
// clips send people (tracked with the Clipbait pixel) plus the comment
// trigger word and end card; views campaigns skip those.
const OBJECTIVES = [["views", "Views", "👀"], ["leads", "Leads", "📩"], ["sales", "Sales", "💰"]];
const OBJECTIVE_LABEL = Object.fromEntries(OBJECTIVES.map(([k, l]) => [k, l]));

// Tap-to-pick presets for the clip checklist (streamer clipping norms).
const STYLE_OPTIONS = [
  "Funny moments", "Reactions", "Rage & tilt", "Clutch plays", "Fails", "Big wins",
  "Hot takes", "Storytime", "Chat interactions", "Collabs & guests", "IRL moments", "Emotional moments",
];
// Rules aren't asked: every brief gets the same short standard set (the AI
// handles clip-level choices). An older brief keeps its own rules.
const standardDos = (name) => [`${name} is the main focus of every clip`, "Vertical 9:16 with captions"];
const STANDARD_DONTS = [
  "No botted or bought views", "No negative or misleading edits",
  "No logos or watermarks", "No reposting other clippers' clips",
];
const KEEP_UP_OPTIONS = [[7, "1 week"], [14, "2 weeks"], [30, "30 days"], [60, "60 days"], [90, "90 days"]];
const MAX_PAYOUT_OPTIONS = [["", "No cap"], ["100", "$100"], ["250", "$250"], ["500", "$500"], ["1000", "$1,000"]];
const MIN_PAYOUT_OPTIONS = [["", "None"], ["5", "$5"], ["10", "$10"], ["25", "$25"]];
const FUND_PROMPT_EXAMPLE = "I want to fund a campaign for Adam Brandon for $7k";
const PAY_TYPE_OPTIONS = [["cpm", "Per 1,000 views"], ["pool", "Share of a pool"]];
const POOL_DAY_OPTIONS = [[7, "1 week"], [14, "2 weeks"], [30, "1 month"], [60, "2 months"]]; // mirrors routes/campaigns.js
const DURATION_OPTIONS = [[0, "No end date"], [14, "2 weeks"], [30, "1 month"], [60, "2 months"], [90, "3 months"]];
const KEYWORD_OPTIONS = [["", "None"], ["LINK", "LINK"], ["LIVE", "LIVE"], ["CLIPS", "CLIPS"]];

// Everything in the flow is tap-to-pick; these build the options from the
// streamer's name so nobody has to type.
const setupOptions = (name) => {
  const base = String(name || "").toLowerCase().replace(/[^a-z0-9_.]+/g, "");
  return [["Clips", "clips"], ["Moments", "moments"], ["Highlights", "highlights"], ["Daily", "daily"]]
    .map(([word, slug]) => ({ displayName: `${name} ${word}`, username: `@${base}.${slug}` }));
};
const disclaimerOptions = (name) => [
  `Fan page, not ${name}'s official account`,
  `Unofficial ${name} clips`,
  `Fan-run clips of ${name}`,
];
const tagOptions = (name, category, platforms) => {
  const base = String(name || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
  const live = platforms.includes("youtube") ? "#youtubegaming" : "#twitch";
  return [...new Set([base && `#${base}`, base && `#${base}clips`, "#clips", "#fyp", "#streamer",
    `#${String(category || "gaming").toLowerCase()}`, live, "#kick", "#viral"].filter(Boolean))];
};
// A dollar amount from an older brief that isn't a preset still shows as picked.
const withCurrent = (opts, v) => (v && !opts.some(([x]) => String(x) === String(v)) ? [...opts, [v, fmtMoney(v)]] : opts);
const endDateFor = (days) => (days ? new Date(Date.now() + days * 864e5).toISOString().slice(0, 10) : "");
const daysUntil = (date) => (date ? Math.round((new Date(date) - Date.now()) / 864e5) : 0);

const EMPTY = {
  streamerName: "", brandName: "", title: "", description: "", category: "Gaming",
  fundPrompt: "", fundingOther: false, brandId: "",
  objective: "views", destinationUrl: "",
  overview: "", goals: "",
  displayName: "", username: "", bioDisclaimer: "", bioLink: "", setupNotes: "",
  platforms: ["tiktok", "instagram", "facebook"], usOnly: true, keepUpDays: 30, audienceNotes: "",
  styles: "", focus: "", hashtags: "", pinnedComment: "", manychatKeyword: "", dos: "", donts: "",
  pickedVods: [], pasteUrl: "", brandLogoUrl: "",
  payType: "cpm", poolDays: 30,
  payoutPer1K: "1", minPayout: "", maxPayout: "", totalBudget: "1000", expiresAt: "",
  submitUrl: "", submitSteps: "",
  clippingGuidance: "",
};

const lines = (v) => String(v || "").split("\n").map((x) => x.trim()).filter(Boolean);
const unlines = (a) => (a || []).join("\n");

function fromCampaign(c) {
  const b = c.brief || {};
  const ck = b.checklist || {};
  return {
    ...EMPTY,
    streamerName: c.streamerName || "", brandName: c.brandName || "", title: c.title || "",
    description: c.description || "", category: c.category || "Other",
    objective: c.objective || "views", destinationUrl: c.destinationUrl || "",
    overview: b.overview || "", goals: unlines(b.goals),
    displayName: b.pageSetup?.displayName || "", username: b.pageSetup?.username || "",
    bioDisclaimer: b.pageSetup?.bioDisclaimer || "", bioLink: b.pageSetup?.bioLink || "", setupNotes: b.pageSetup?.notes || "",
    platforms: c.platforms || [], usOnly: b.audience?.usOnly !== false, keepUpDays: b.audience?.keepUpDays ?? 30,
    audienceNotes: b.audience?.notes || "",
    styles: unlines(ck.styles), focus: unlines(ck.focus), hashtags: (ck.hashtags || []).join(" "),
    pinnedComment: ck.pinnedComment || "", manychatKeyword: ck.manychatKeyword || "",
    dos: unlines(ck.dos), donts: unlines(ck.donts),
    pickedVods: (c.sources || []).map((s) => ({ url: s.url, title: s.label || "", thumbnail: s.thumbnail || "" })),
    payType: c.payType || "cpm", poolDays: c.pool?.days || 30,
    payoutPer1K: String(c.payoutPer1K ?? ""), minPayout: c.pay?.minPayout ? String(c.pay.minPayout) : "",
    maxPayout: c.pay?.maxPayout ? String(c.pay.maxPayout) : "", totalBudget: String(c.totalBudget ?? ""),
    expiresAt: c.expiresAt ? String(c.expiresAt).slice(0, 10) : "",
    submitUrl: c.submitUrl || "", submitSteps: unlines(c.submitSteps),
    clippingGuidance: c.clippingGuidance || "",
  };
}

// Title, pitch and overview are written for the launcher from their picks.
const autoTitle = (f) => f.title || `${f.streamerName || f.brandName} Clipping`;
const autoDescription = (f) => f.description || (f.payType === "pool"
  ? `Clip ${f.streamerName || f.brandName}'s best moments and earn your share of the pool.`
  : `Clip ${f.streamerName || f.brandName}'s best moments and get paid per view.`);
const autoOverview = (f) => {
  if (f.overview) return f.overview;
  const name = f.streamerName || f.brandName;
  const goals = lines(f.goals).map((g) => g.toLowerCase());
  const styles = lines(f.styles).map((s) => s.toLowerCase());
  return [
    f.payType === "pool"
      ? `${name} is splitting a pool between clippers by views on their best moments.`
      : `${name} is paying clippers per view to post their best moments.`,
    goals.length ? `The goal: ${goals.join(", ")}.` : "",
    styles.length ? `Clips that win: ${styles.join(", ")}.` : "",
  ].filter(Boolean).join(" ");
};

function toPayload(f) {
  return {
    streamerName: f.streamerName,
    brandName: f.brandName || f.streamerName,
    title: autoTitle(f),
    objective: f.objective,
    destinationUrl: f.objective === "views" ? "" : f.destinationUrl.trim(),
    description: autoDescription(f),
    category: f.category,
    platforms: f.platforms,
    payType: f.payType,
    poolDays: f.poolDays,
    useConnectedChannel: !f.fundingOther,
    brandId: f.brandId || undefined,
    payoutPer1K: f.payoutPer1K,
    totalBudget: f.totalBudget,
    expiresAt: f.expiresAt || null,
    pay: { minPayout: f.minPayout || 0, maxPayout: f.maxPayout || 0 },
    submitUrl: f.submitUrl,
    submitSteps: lines(f.submitSteps),
    brief: {
      overview: autoOverview(f),
      goals: [OBJECTIVE_LABEL[f.objective] || "Views"],
      pageSetup: { displayName: f.displayName, username: f.username, bioDisclaimer: f.bioDisclaimer, bioLink: f.bioLink === "none" ? "" : f.bioLink, notes: f.setupNotes },
      audience: { usOnly: f.usOnly, keepUpDays: f.keepUpDays, notes: f.audienceNotes },
      checklist: {
        styles: lines(f.styles), focus: lines(f.focus),
        hashtags: f.hashtags.split(/[\s,]+/).filter(Boolean),
        pinnedComment: f.pinnedComment, manychatKeyword: f.manychatKeyword,
        dos: lines(f.dos).length ? lines(f.dos) : standardDos(f.streamerName || f.brandName),
        donts: lines(f.donts).length ? lines(f.donts) : STANDARD_DONTS,
      },
    },
    sources: (f.pickedVods || []).map((v) => ({ url: v.url, label: v.title, thumbnail: v.thumbnail })),
    brandLogoUrl: f.brandLogoUrl,
    clippingGuidance: f.clippingGuidance,
  };
}

function Input({ label, hint, area, rows = 4, autoFocus, ...props }) {
  return (
    <label className="cmp-input">
      {label && <span className="cmp-input-label">{label}</span>}
      {area ? <textarea rows={rows} autoFocus={autoFocus} {...props} /> : <input autoFocus={autoFocus} {...props} />}
      {hint && <span className="cmp-input-hint">{hint}</span>}
    </label>
  );
}

function FilePick({ label, accept, file, existing, url, onFile, round }) {
  const preview = useMemo(() => (file && file.type.startsWith("image/") ? URL.createObjectURL(file) : null), [file]);
  const img = preview || url || (existing && /\.(png|jpe?g|gif|webp)(\?|$)/i.test(existing) ? existing : null);
  return (
    <label className={`cmp-file${round ? " round" : ""}`}>
      <span className="cmp-input-label">{label}</span>
      <div className="cmp-file-box">
        {img ? <img src={img} alt="" /> : <span>{file ? file.name : existing ? "Uploaded" : "+ Add"}</span>}
      </div>
      <input type="file" accept={accept} onChange={(e) => onFile(e.target.files?.[0] || null)} />
    </label>
  );
}

// A big tappable row with a radio/check on the right (Instagram style).
function Choice({ title, on, onClick, icon, multi }) {
  return (
    <button type="button" className={`cmp-choice${on ? " on" : ""}`} onClick={onClick}>
      {icon && <span className="cmp-choice-icon">{icon}</span>}
      <span className="cmp-choice-text">
        <span className="cmp-choice-title">{title}</span>
      </span>
      <span className={`cmp-choice-mark${multi ? " multi" : ""}`}>{on && (multi ? "✓" : "")}</span>
    </button>
  );
}

// Multi-select chips (values = newline list). Items picked in an older brief
// that aren't presets still show so they can be unpicked.
function Chips({ label, options, value, onChange }) {
  const picked = lines(value);
  const all = [...options, ...picked.filter((x) => !options.includes(x))];
  const toggle = (x) => onChange(unlines(picked.includes(x) ? picked.filter((y) => y !== x) : [...picked, x]));
  return (
    <div className="cmp-chipset">
      {label && <span className="cmp-input-label">{label}</span>}
      <div className="cmp-chipset-row">
        {all.map((x) => (
          <button type="button" key={x} className={`cmp-pick${picked.includes(x) ? " on" : ""}`} onClick={() => toggle(x)}>
            {picked.includes(x) && <span aria-hidden="true">✓</span>}{x}
          </button>
        ))}
      </div>
    </div>
  );
}

// Single-select chips. options = [[value, label]].
function Pills({ label, options, value, onChange }) {
  return (
    <div className="cmp-chipset">
      {label && <span className="cmp-input-label">{label}</span>}
      <div className="cmp-chipset-row">
        {options.map(([v, text]) => (
          <button type="button" key={String(v)} className={`cmp-pick${String(value) === String(v) ? " on" : ""}`} onClick={() => onChange(v)}>
            {text}
          </button>
        ))}
      </div>
    </div>
  );
}

function Slider({ label, prefix, value, onChange, min, max, step, suffix }) {
  const n = Number(value) || 0;
  const pct = Math.max(0, Math.min(100, ((Math.min(n, max) - min) / (max - min)) * 100));
  return (
    <div className="cmp-slider">
      <div className="cmp-slider-head">
        <span className="cmp-slider-label">{label}</span>
        <span className="cmp-slider-box">
          {prefix && <span>{prefix}</span>}
          <strong>{n.toLocaleString(undefined, { maximumFractionDigits: 2 })}</strong>
          {suffix && <span className="cmp-slider-suffix">{suffix}</span>}
        </span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={Math.min(n, max)}
        style={{ "--pct": `${pct}%` }}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

// The profile's channel link belongs to the active streamer only.
const profileLinkApplies = (f) => !f.fundingOther && !f.brandId;

export default function LaunchCampaignPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const editId = sp.get("edit");
  const [f, setF] = useState(() => {
    if (editId) return EMPTY;
    try {
      const d = { ...EMPTY, ...JSON.parse(localStorage.getItem(DRAFT_KEY) || "{}") };
      return { ...d, totalBudget: String(Math.max(MIN_POOL, Number(d.totalBudget) || 0)) };
    } catch { return EMPTY; }
  });
  const [existing, setExisting] = useState(null);
  const [files, setFiles] = useState({ brandLogo: null, bannerImage: null, endCard: null });
  const [channel, setChannel] = useState(null);
  const [profile, setProfile] = useState(null); // streamer name, avatar, channel link
  const [myVods, setMyVods] = useState(null);
  const [savedStreamers, setSavedStreamers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(0);
  const topRef = useRef(null);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));

  useEffect(() => {
    if (editId) {
      axiosInstance.get(`/campaigns/${editId}`)
        .then((r) => { setExisting(r.data); setF(fromCampaign(r.data)); })
        .catch(() => toast.error("Couldn't load that campaign"));
      return;
    }
    // The launcher's connected channel becomes a clip source automatically.
    axiosInstance.get("/users/me/creator-settings").then((r) => {
      const sc = r.data?.socialConnections || {};
      const ch = sc.twitch?.connected ? { platform: "twitch", name: sc.twitch.username }
        : sc.youtube?.connected ? { platform: "youtube", name: sc.youtube.channelName || sc.youtube.channelTitle }
        : sc.kick?.connected ? { platform: "kick", name: sc.kick.username } : null;
      if (ch && !ch.name) ch.name = `your ${PLATFORM_META[ch.platform]?.label || ""} channel`.replace("  ", " ");
      setChannel(ch);
      const cs = r.data?.creatorSettings || {};
      const conn = ch ? sc[ch.platform] : null;
      const handle = cs.channelUsername || conn?.username || "";
      const host = { twitch: "twitch.tv", kick: "kick.com", youtube: "youtube.com/@", rumble: "rumble.com" }[cs.platform || ch?.platform];
      const p = {
        name: r.data?.streamerName || conn?.displayName || handle || "",
        handle,
        avatar: conn?.avatar || r.data?.profilePicture || "",
        link: host && handle ? `https://${host}${host.endsWith("@") ? "" : "/"}${handle}` : "",
      };
      setProfile(p);
      // Start from what we know about the streamer; everything stays editable.
      setF((prev) => ({
        ...prev,
        streamerName: prev.streamerName || p.name,
        // Funding someone else: their campaign doesn't get the launcher's photo.
        brandLogoUrl: prev.fundingOther || prev.brandId ? prev.brandLogoUrl : prev.brandLogoUrl || p.avatar,
      }));
    }).catch(() => {});
    axiosInstance.get("/users/me/brands").then((r) => setSavedStreamers(r.data.brands || [])).catch(() => {});
  }, [editId]);

  // Streams come from the picked saved streamer, or else the active one.
  useEffect(() => {
    if (editId) return undefined;
    let stale = false;
    setMyVods(null);
    axiosInstance.get("/campaigns/my-vods", { params: f.brandId ? { brand: f.brandId } : {} })
      .then((r) => {
        if (stale) return;
        setMyVods(r.data.vods || []);
        if (f.brandId) setChannel(r.data.channel || null);
      })
      .catch(() => { if (!stale) setMyVods([]); });
    return () => { stale = true; };
  }, [editId, f.brandId]);

  // Keep a draft so leaving mid-flow doesn't lose the brief (images aren't kept).
  useEffect(() => {
    if (editId) return;
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(f)); } catch { /* ignore */ }
  }, [f, editId]);

  const pool = Math.max(0, Number(f.totalBudget) || 0);
  const total = pool; // the 30% comes out of clipper payouts, not at checkout
  const parsedFund = parseFundPrompt(f.fundPrompt);
  // Fills in the creator and amount as they type. Someone other than the
  // launcher's own channel gets no launcher photo, streams or channel.
  const pickStreamer = (b) => setF((p) => ({
    ...p,
    brandId: b._id,
    fundingOther: false,
    fundPrompt: parseFundPrompt(p.fundPrompt).name ? "" : p.fundPrompt,
    streamerName: b.name,
    brandName: "",
    brandLogoUrl: b.avatar || "",
    bioLink: "",
    pickedVods: p.brandId === b._id ? p.pickedVods : [],
  }));
  const onFundPrompt = (e) => {
    const text = e.target.value;
    const { name: who, amount } = parseFundPrompt(text);
    const saved = who ? savedStreamers.find((b) => b.name.toLowerCase() === who.toLowerCase()) : null;
    const other = !!who && !saved && who.toLowerCase() !== String(profile?.name || "").toLowerCase();
    setF((p) => ({
      ...p,
      fundPrompt: text,
      fundingOther: other,
      // Only an amount typed: keep the streamer they tapped.
      brandId: saved ? saved._id : who ? "" : p.brandId,
      streamerName: saved?.name || who || (p.brandId ? p.streamerName : profile?.name || ""),
      brandName: "",
      brandLogoUrl: saved ? saved.avatar || "" : other ? "" : p.brandLogoUrl || profile?.avatar || "",
      pickedVods: other && !p.fundingOther ? [] : p.pickedVods,
      totalBudget: amount ? String(amount) : p.totalBudget,
    }));
  };
  const cpm = Number(f.payoutPer1K) || 0;
  const payPool = f.payType === "pool";
  const estViews = cpm > 0 && !payPool ? Math.round((pool / cpm) * 1000) : 0;

  const togglePlatform = (p) => setF((prev) => ({
    ...prev,
    platforms: prev.platforms.includes(p) ? prev.platforms.filter((x) => x !== p) : [...prev.platforms, p],
  }));

  const name = f.streamerName || f.brandName;
  const wantsResults = f.objective !== "views";
  // Page setup starts on the first suggestion for the streamer's name.
  const setups = name ? setupOptions(name) : [];
  const suggestSetup = () => setF((prev) => ({
    ...prev,
    displayName: prev.displayName || setups[0]?.displayName || "",
    username: prev.username || setups[0]?.username || "",
    bioDisclaimer: prev.bioDisclaimer || (name ? disclaimerOptions(name)[0] : ""),
    bioLink: prev.bioLink || (profileLinkApplies(prev) ? profile?.link : "") || "",
  }));
  const setupList = f.displayName && !setups.some((o) => o.displayName === f.displayName)
    ? [...setups, { displayName: f.displayName, username: f.username }] : setups;
  const disclaimers = name ? disclaimerOptions(name) : [];
  const disclaimerList = f.bioDisclaimer && !disclaimers.includes(f.bioDisclaimer) ? [...disclaimers, f.bioDisclaimer] : disclaimers;
  const bioLink = f.bioLink || (profileLinkApplies(f) ? profile?.link : "") || "";
  const setTags = (v) => setF((prev) => ({ ...prev, hashtags: lines(v).join(" ") }));
  const pinnedFor = (kw) => (kw ? `Comment ${kw} and I'll send you the link` : "");

  const isPicked = (url) => f.pickedVods.some((v) => v.url === url);
  const toggleVod = (v) => setF((prev) => ({
    ...prev,
    pickedVods: prev.pickedVods.some((x) => x.url === v.url)
      ? prev.pickedVods.filter((x) => x.url !== v.url)
      : [...prev.pickedVods, { url: v.url, title: v.title || "", thumbnail: v.thumbnail || "" }],
  }));
  const addPasted = () => {
    const url = f.pasteUrl.trim();
    if (!/^https?:\/\//i.test(url)) return toast.error("Paste a full VOD link (https://…).");
    const yt = url.match(/(?:v=|youtu\.be\/|live\/|shorts\/)([\w-]{11})/);
    const thumbnail = yt ? `https://i.ytimg.com/vi/${yt[1]}/mqdefault.jpg` : "";
    setF((prev) => ({ ...prev, pasteUrl: "", pickedVods: prev.pickedVods.some((x) => x.url === url) ? prev.pickedVods : [...prev.pickedVods, { url, title: "", thumbnail }] }));
  };

  // Each screen asks one thing, all by tapping. `check` returns an error or "".
  const steps = [
    {
      key: "fund", nav: "Fund", when: () => !editId,
      q: "What do you want to fund?",
      check: () => (!f.brandId && !parsedFund.name && !parsedFund.amount
        ? `Try: ${FUND_PROMPT_EXAMPLE}`
        : parsedFund.amount && parsedFund.amount < MIN_POOL ? `The minimum clipper pool is ${fmtMoney(MIN_POOL)}.` : ""),
      body: (
        <div className="cmp-fund-prompt">
          {savedStreamers.length > 0 && (
            <div className="cmp-choices">
              {savedStreamers.map((b) => (
                <Choice
                  key={b._id}
                  title={b.name}
                  on={f.brandId === b._id}
                  onClick={() => pickStreamer(b)}
                  icon={b.avatar
                    ? <img className="cmp-choice-avatar" src={b.avatar} alt="" />
                    : <span className="cmp-choice-avatar">{b.name?.[0]?.toUpperCase()}</span>}
                />
              ))}
            </div>
          )}
          <Input autoFocus={savedStreamers.length === 0} value={f.fundPrompt} onChange={onFundPrompt} placeholder={FUND_PROMPT_EXAMPLE} />
          {(parsedFund.name || parsedFund.amount > 0) && (
            <div className="cmp-chipset-row">
              {parsedFund.name && !f.brandId && <span className="cmp-pick on">{parsedFund.name}</span>}
              {parsedFund.amount > 0 && <span className="cmp-pick on">{fmtMoney(parsedFund.amount)}</span>}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "objective", nav: "Objective",
      q: "What do you want from clippers?",
      check: () => "",
      body: (
        <div className="cmp-choices">
          {OBJECTIVES.map(([k, label, icon]) => (
            <Choice key={k} icon={<span className="cmp-choice-emoji">{icon}</span>} title={label} on={f.objective === k} onClick={() => setF((p) => ({ ...p, objective: k }))} />
          ))}
        </div>
      ),
    },
    {
      key: "basics", nav: "Campaign",
      q: "Who are clippers clipping?",
      check: () => (!name ? "Connect your Twitch, Kick or YouTube in Me first." : ""),
      body: (
        <>
          {name ? (
            <div className="cmp-choices">
              <Choice
                title={name}
                icon={(files.brandLogo || f.brandLogoUrl || existing?.brandLogo)
                  ? <img className="cmp-choice-avatar" src={files.brandLogo ? URL.createObjectURL(files.brandLogo) : f.brandLogoUrl || existing?.brandLogo} alt="" />
                  : <span className="cmp-choice-avatar">{name[0]?.toUpperCase()}</span>}
                on
                onClick={() => {}}
              />
            </div>
          ) : (
            <div className="cmp-note">
              Connect your channel so we can set up the campaign for you.{" "}
              <button type="button" className="cmp-link" onClick={() => navigate("/me")}>Go to Me</button>
            </div>
          )}
          {f.fundingOther && (
            <FilePick label="Creator photo" accept="image/*" round file={files.brandLogo} onFile={(v) => setFiles((p) => ({ ...p, brandLogo: v }))} />
          )}
          <FilePick label="Banner" accept="image/png,image/jpeg,image/webp,image/gif" file={files.bannerImage} existing={existing?.bannerImage} onFile={(v) => setFiles((p) => ({ ...p, bannerImage: v }))} />
          <Pills label="Category" options={CATEGORIES.map((c) => [c, c])} value={f.category} onChange={set("category")} />
        </>
      ),
    },
    {
      key: "platforms", nav: "Platforms",
      q: "Where should clips be posted?",
      check: () => (!f.platforms.length ? "Pick at least one platform." : ""),
      body: (
        <>
          <div className="cmp-choices">
            {POST_PLATFORMS.map((p) => (
              <Choice key={p} multi icon={PLATFORM_META[p]?.icon(20)} title={PLATFORM_POST_LABEL[p]} on={f.platforms.includes(p)} onClick={() => togglePlatform(p)} />
            ))}
          </div>
          <Pills label="Which views count?" options={[[true, "US views only"], [false, "All views"]]} value={f.usOnly} onChange={(v) => setF((p) => ({ ...p, usOnly: v }))} />
          {!payPool && <Pills label="Keep posts up for" options={KEEP_UP_OPTIONS} value={f.keepUpDays} onChange={set("keepUpDays")} />}
        </>
      ),
    },
    {
      key: "setup", nav: "Page setup", optional: true,
      q: "What should clip pages be called?",
      check: () => "",
      body: (
        <>
          <div className="cmp-choices">
            {setupList.map((o) => (
              <Choice key={o.displayName} title={o.displayName} on={f.displayName === o.displayName}
                onClick={() => setF((p) => ({ ...p, displayName: o.displayName, username: o.username }))} />
            ))}
          </div>
          {disclaimerList.length > 0 && (
            <Pills label="Bio line" options={[...disclaimerList.map((d) => [d, d]), ["", "None"]]} value={f.bioDisclaimer} onChange={set("bioDisclaimer")} />
          )}
          {bioLink && (
            <Pills label="Bio link" options={[[bioLink, bioLink.replace(/^https?:\/\//, "")], ["none", "No link"]]}
              value={f.bioLink === "none" ? "none" : bioLink} onChange={set("bioLink")} />
          )}
        </>
      ),
    },
    {
      key: "checklist", nav: "Clip style", optional: true,
      q: "What moments should clippers post?",
      check: () => "",
      body: (
        <Chips options={STYLE_OPTIONS} value={f.styles} onChange={set("styles")} />
      ),
    },
    {
      key: "hashtags", nav: "Hashtags", optional: true,
      q: "Which hashtags should clips use?",
      check: () => "",
      body: (
        <Chips options={tagOptions(name, f.category, f.platforms)} value={unlines(f.hashtags.split(/[\s,]+/).filter(Boolean))} onChange={setTags} />
      ),
    },
    {
      key: "results", nav: OBJECTIVE_LABEL[f.objective], when: () => wantsResults,
      q: f.objective === "sales" ? "Where do clips send buyers?" : "Where do clips send leads?",
      check: () => (wantsResults && !/^https?:\/\/\S+\.\S+/i.test(f.destinationUrl.trim()) ? "Paste the link clips should send people to." : ""),
      body: (
        <>
          <Input autoFocus label="Paste your link" value={f.destinationUrl} onChange={set("destinationUrl")} placeholder="https://yourstore.com" />
          <Pills label="Comment trigger word" options={KEYWORD_OPTIONS} value={f.manychatKeyword}
            onChange={(v) => setF((p) => ({ ...p, manychatKeyword: v, pinnedComment: pinnedFor(v) }))} />
          <FilePick label="End card" accept="image/*,video/*" file={files.endCard} existing={existing?.brief?.endCard?.url} onFile={(v) => setFiles((p) => ({ ...p, endCard: v }))} />
        </>
      ),
    },
    {
      key: "sources", nav: "Streams", optional: true,
      q: "Which streams can clippers clip?",
      check: () => "",
      body: (
        <>
          {!f.fundingOther && myVods === null && !editId && <div className="cmp-loading small">Loading your streams…</div>}
          {!f.fundingOther && myVods?.length > 0 && (
            <div className="cmp-choices">
              {myVods.map((v) => (
                <button type="button" key={v.id || v.url} className={`cmp-vod${isPicked(v.url) ? " on" : ""}`} onClick={() => toggleVod(v)}>
                  {v.thumbnail ? <img src={v.thumbnail} alt="" /> : <span className="cmp-vod-ph" />}
                  <span className="cmp-choice-text">
                    <span className="cmp-choice-title">{v.title}</span>
                  </span>
                  <span className="cmp-choice-mark multi">{isPicked(v.url) && "✓"}</span>
                </button>
              ))}
            </div>
          )}
          {!f.fundingOther && myVods?.length === 0 && !editId && (
            <div className="cmp-note">{channel ? `We couldn't load streams from ${channel.name} right now.` : "Connect your Twitch, Kick or YouTube in Me to pick streams here."} You can paste a VOD link instead.</div>
          )}
          {f.pickedVods.filter((v) => !myVods?.some((m) => m.url === v.url)).map((v) => (
            <div key={v.url} className="cmp-vod on static">
              {v.thumbnail ? <img src={v.thumbnail} alt="" /> : <span className="cmp-vod-ph" />}
              <span className="cmp-choice-text"><span className="cmp-choice-title">{v.title || v.url}</span></span>
              <button type="button" className="cmp-link" onClick={() => toggleVod(v)}>Remove</button>
            </div>
          ))}
          <div className="cmp-paste">
            <Input label="Paste a VOD link" value={f.pasteUrl} onChange={set("pasteUrl")} placeholder="Twitch, Kick, YouTube or Google Drive link"
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); addPasted(); } }} />
            <button type="button" className="cmp-btn ghost" onClick={addPasted}>Add</button>
          </div>
        </>
      ),
    },
    {
      key: "budget", nav: "Budget",
      q: "What's your clipper budget?",
      check: () => (!payPool && !(cpm > 0) ? "Set what you pay per 1,000 views." : !editId && pool < MIN_POOL ? `The minimum clipper pool is ${fmtMoney(MIN_POOL)}.` : ""),
      summary: true,
      body: (
        <>
          {!editId && <Pills label="Pay clippers" options={PAY_TYPE_OPTIONS} value={f.payType} onChange={set("payType")} />}
          {!editId && <Slider label="Clipper pool" prefix="$" value={f.totalBudget} onChange={set("totalBudget")} min={MIN_POOL} max={Math.max(50000, pool)} step={100} />}
          {payPool ? (
            !editId && <Pills label="Pool runs for" options={POOL_DAY_OPTIONS} value={f.poolDays} onChange={set("poolDays")} />
          ) : (
            <>
              <Slider label="Pay per 1,000 views" prefix="$" value={f.payoutPer1K} onChange={set("payoutPer1K")} min={0.25} max={10} step={0.25} />
              <Pills label="Max payout per clip" options={withCurrent(MAX_PAYOUT_OPTIONS, f.maxPayout)} value={f.maxPayout} onChange={set("maxPayout")} />
              <Pills label="Min payout per clip" options={withCurrent(MIN_PAYOUT_OPTIONS, f.minPayout)} value={f.minPayout} onChange={set("minPayout")} />
              <Pills label="Runs for" options={DURATION_OPTIONS}
                value={DURATION_OPTIONS.some(([d]) => d === daysUntil(f.expiresAt)) ? daysUntil(f.expiresAt) : f.expiresAt ? "custom" : 0}
                onChange={(d) => setF((p) => ({ ...p, expiresAt: endDateFor(d) }))} />
            </>
          )}
        </>
      ),
    },
    { key: "review", nav: "Review", q: "Everything look good?", review: true },
  ].filter((x) => !x.when || x.when());

  const cur = steps[step];
  const last = steps.length - 1;
  const go = (i) => {
    if (steps[i]?.key === "setup" && !editId) suggestSetup();
    setStep(Math.max(0, Math.min(last, i)));
    topRef.current?.scrollIntoView({ block: "start" });
  };
  const next = () => {
    const err = cur.check?.();
    if (err) return toast.error(err);
    go(step + 1);
  };
  const back = () => (step === 0 ? navigate(editId ? -1 : "/campaigns/dashboard") : go(step - 1));

  const submit = async () => {
    const bad = steps.findIndex((s) => s.check && s.check());
    if (bad >= 0) { go(bad); return toast.error(steps[bad].check()); }
    const fd = new FormData();
    fd.append("data", JSON.stringify(toPayload(f)));
    Object.entries(files).forEach(([k, v]) => { if (v) fd.append(k, v); });
    setSaving(true);
    try {
      if (editId) {
        const r = await axiosInstance.put(`/campaigns/${editId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Brief saved");
        navigate(briefPath(r.data));
      } else {
        const r = await axiosInstance.post("/campaigns/launch", fd, { headers: { "Content-Type": "multipart/form-data" } });
        try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
        window.location.href = r.data.checkoutUrl; // Stripe Checkout
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || "Something went wrong. Try again.");
      setSaving(false);
    }
  };

  // Enter moves on from single-line inputs (textareas keep newlines).
  const onKeyDown = (e) => {
    if (e.key === "Enter" && e.target.tagName === "INPUT" && e.target.type !== "file" && !cur.review) {
      e.preventDefault();
      next();
    }
  };

  const reviewRows = [
    ["basics", "Campaign", [name, f.category].filter(Boolean).join(" · ")],
    ["objective", "Objective", OBJECTIVE_LABEL[f.objective]],
    ["platforms", "Platforms & audience", `${f.platforms.map((p) => PLATFORM_POST_LABEL[p]).join(", ")} · ${f.usOnly ? "US views only" : "All views"}${payPool ? "" : ` · keep up ${f.keepUpDays} days`}`],
    ["setup", "Page setup", [f.displayName, f.username].filter(Boolean).join(" · ") || "Not set"],
    ["checklist", "Clip style", lines(f.styles).slice(0, 4).join(", ") || "Not set"],
    ["hashtags", "Hashtags", f.hashtags || "None"],
    ...(wantsResults ? [["results", OBJECTIVE_LABEL[f.objective], f.destinationUrl.replace(/^https?:\/\//, "") || "Not set"]] : []),
    ["sources", "Streams", `${f.pickedVods.length} picked${channel && !editId && !f.fundingOther ? ` + latest from ${channel.name}` : ""}`],
    ["budget", "Pay", payPool ? `Share of ${fmtMoney(pool)} pool${editId ? "" : ` · ${POOL_DAY_OPTIONS.find(([d]) => d === Number(f.poolDays))?.[1] || ""}`}` : `${fmtMoney(cpm, { cents: cpm % 1 !== 0 })} per 1,000 views${f.maxPayout ? ` · max ${fmtMoney(f.maxPayout)}/clip` : ""}`],
  ];

  return (
    <div className="cmp-flow" onKeyDown={onKeyDown}>
      <div ref={topRef} />
      <header className="cmp-flow-top">
        <button type="button" className="cmp-flow-back" onClick={back} aria-label={step === 0 ? "Cancel" : "Back"}>
          {step === 0 ? "Cancel" : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
          )}
        </button>
        <div className="cmp-flow-title">{editId ? "Edit campaign" : cur.nav}</div>
        <div className="cmp-flow-count">{step + 1}/{steps.length}</div>
      </header>
      <div className="cmp-flow-progress" role="progressbar" aria-valuemin={1} aria-valuemax={steps.length} aria-valuenow={step + 1}>
        {steps.map((s, i) => (
          <button type="button" key={s.key} className={i <= step ? "on" : ""} onClick={() => i < step && go(i)} aria-label={s.nav} tabIndex={i < step ? 0 : -1} />
        ))}
      </div>

      <main className="cmp-flow-body" key={cur.key}>
        <h1 className="cmp-flow-q">{cur.q}</h1>

        {!cur.review && <div className="cmp-flow-fields">{cur.body}</div>}

        {cur.review && (
          <div className="cmp-review-preview">
            <div className="cmp-review-id">
              <div className="cmp-review-avatar">
                {(files.brandLogo || f.brandLogoUrl || existing?.brandLogo)
                  ? <img src={files.brandLogo ? URL.createObjectURL(files.brandLogo) : f.brandLogoUrl || existing?.brandLogo} alt="" />
                  : <span>{name?.[0]?.toUpperCase()}</span>}
              </div>
              <div className="cmp-review-names">
                <span>{name}</span>
                <strong>{autoTitle(f)}</strong>
              </div>
            </div>
          </div>
        )}

        {cur.review && (
          <div className="cmp-review">
            {reviewRows.map(([key, label, value]) => (
              <button type="button" key={key} className="cmp-review-row" onClick={() => go(steps.findIndex((s) => s.key === key))}>
                <span className="cmp-review-text">
                  <span className="cmp-review-label">{label}</span>
                  <span className="cmp-review-value">{value}</span>
                </span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6" /></svg>
              </button>
            ))}
          </div>
        )}
      </main>

      <footer className="cmp-flow-foot">
        {(cur.summary || cur.review) && !editId && (
          <div className="cmp-flow-summary">
            <div><span>Clipper pool</span><strong>{fmtMoney(pool)}</strong></div>
            {estViews > 0 && <div><span>Paid views</span><strong className="soft">up to {estViews.toLocaleString()}</strong></div>}
            <div><span>Platform fee</span><strong className="soft">{Math.round(PLATFORM_FEE_RATE * 100)}% of clipper payouts</strong></div>
            {cur.review && <div className="total"><span>Total today</span><strong>{fmtMoney(total, { cents: total % 1 !== 0 })}</strong></div>}
          </div>
        )}
        {cur.review ? (
          <button type="button" className="cmp-btn big block" onClick={submit} disabled={saving}>
            {saving ? "Working…" : editId ? "Save brief" : `Fund & launch · ${fmtMoney(total, { cents: total % 1 !== 0 })}`}
          </button>
        ) : (
          <div className="cmp-flow-actions">
            {cur.optional && <button type="button" className="cmp-flow-skip" onClick={() => go(step + 1)}>Skip</button>}
            <button type="button" className="cmp-btn big block" onClick={next}>Next</button>
          </div>
        )}
        {cur.review && !editId && <p className="cmp-flow-fine">You'll pay on Stripe. The campaign goes live as soon as payment clears.</p>}
      </footer>
    </div>
  );
}
